/**
 * Status and basis chips. The WORD always carries the meaning; the tint only supports it (two of the
 * status steps sit under 3:1 on a light surface, so colour alone would fail).
 */

type Tone = 'success' | 'info' | 'warning' | 'danger' | 'muted';

const TONES: Record<string, Tone> = {
  // success
  done: 'success', active: 'success', ok: 'success', approved: 'success', paid: 'success', granted: 'success',
  succeeded: 'success', published: 'success', enabled: 'success', delivered: 'success', completed: 'success',
  available: 'success', in_force: 'success',
  // finished, but not everything applied — never a plain success (§4.1)
  done_with_warnings: 'warning',
  // in flight
  running: 'info', pending: 'info', pending_payment: 'info', waiting_retry: 'info', submitted: 'info',
  issued: 'info', scheduled: 'info', trialing: 'info', draft: 'info', created: 'info', launched: 'info',
  awaiting: 'info',
  // needs attention
  overdue: 'warning', grant_pending: 'warning', past_due: 'warning', paused: 'warning', halted: 'warning',
  stale: 'warning', warning: 'warning', postpaid: 'warning',
  // broken / refused
  failed: 'danger', rejected: 'danger', grant_failed: 'danger', unavailable: 'danger', error: 'danger',
  suspended: 'danger', churned: 'danger', critical: 'danger',
  // finished, neutral
  skipped: 'muted', cancelled: 'muted', void: 'muted', revoked: 'muted', retired: 'muted', expired: 'muted',
  disabled: 'muted', archived: 'muted', not_configured: 'muted',
};

export function toneOf(status: string | null | undefined): Tone {
  if (!status) return 'muted';
  return TONES[status.toLowerCase()] ?? 'muted';
}

export function humanize(status: string | null | undefined): string {
  if (!status) return '—';
  return status.replace(/_/g, ' ');
}

export function StatusChip({ status, tone, title }: { status: string | null | undefined; tone?: Tone; title?: string }) {
  const t = tone ?? toneOf(status);
  return (
    <span className={`chip chip-${t}`} title={title}>
      {humanize(status)}
    </span>
  );
}

/**
 * The CP3 label. Every attributed number (a tenant's share of shared CPU/RAM/DB time) wears
 * "estimated"; collector figures wear "measured". The two are never allowed to look alike.
 */
export function BasisBadge({ basis }: { basis: string | null | undefined }) {
  if (basis === 'estimated') {
    return (
      <span
        className="chip chip-estimated"
        title="Attributed, not measured: a share of shared containers derived from request and DB-time shares."
      >
        estimated
      </span>
    );
  }
  return (
    <span className="chip chip-measured" title="Measured directly by a collector.">
      measured
    </span>
  );
}

/**
 * The same "estimated" look for a figure that is not a measured attribution but still not a fact — a liability
 * valued at today's price, say. `title` says what it is estimated from.
 */
export function EstimateChip({ title, label = 'estimate' }: { title: string; label?: string }) {
  return <span className="chip chip-estimated" title={title}>{label}</span>;
}

export function SeverityChip({ severity }: { severity: string }) {
  return (
    <span className={`chip ${severity === 'critical' ? 'chip-danger' : 'chip-warning'}`}>
      {severity === 'critical' ? '✕ critical' : '! warning'}
    </span>
  );
}
